import { datadogRum } from '@datadog/browser-rum';
import type {
  AuthErrorRecoveryEvent,
  AuthProviderEvent,
  AuthProviderErrorAction,
  CallbackHandlerEvent,
  LogoutEvent,
  ResolvedTokenSource,
} from '@auth/utils/types';

/**
 * `@auth/monitoring` — Datadog RUM adapter for the `@auth` package.
 *
 * This package is the standard adapter that wires auth callbacks into
 * `datadogRum.addAction` / `addError` / `setUser` with **stable event
 * names shared across all jupiter consumer apps**. Each app should:
 *
 *   1. Initialize RUM as it does today (its own `applicationId`, `service`,
 *      `clientToken`, `sessionSampleRate`, consent gating).
 *   2. Pass the helpers below into the relevant `@auth` props at provider
 *      mount time.
 */

export const AUTH_TOKEN_RESOLVED_EVENT = 'auth.token.resolved' as const;

/**
 * Stable, prefixed event names that land in Datadog RUM as the action name.
 * Build dashboards / monitors against these.
 */
export type DDAuthAction =
  | `auth.${AuthProviderEvent}`
  | `auth.callback.${CallbackHandlerEvent}`
  | `auth.logout.${LogoutEvent}`
  | `auth.recovery.${AuthErrorRecoveryEvent}`
  | typeof AUTH_TOKEN_RESOLVED_EVENT;

/** Action discriminators attached to RUM errors as the `action` tag. */
export type DDAuthErrorAction =
  | AuthProviderErrorAction
  | 'postLegacySession'
  | 'deleteLegacySession'
  | 'callback.notAuthenticated'
  | 'tokenResolve';

const ddReady = (): boolean => {
  try {
    return !!datadogRum?.getInternalContext();
  } catch {
    return false;
  }
};

const safeContext = (
  ctx?: Record<string, unknown>,
): Record<string, unknown> | undefined => (ctx ? { ...ctx } : undefined);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fire a RUM action for any `@auth/*` event
 */
export const trackAuthEvent = (
  action: DDAuthAction,
  ctx?: Record<string, unknown>,
): void => {
  if (!ddReady()) {
    return;
  }
  try {
    datadogRum.addAction(action, safeContext(ctx));
  } catch {
    // RUM transport hiccup — never break the auth flow.
  }
};

/**
 * Report an auth-domain error to RUM
 */
export const reportAuthError = (
  error: unknown,
  action: DDAuthErrorAction,
  ctx?: Record<string, unknown>,
): void => {
  if (!ddReady()) {
    return;
  }
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    datadogRum.addError(err, {
      tags: { domain: 'auth', action },
      extra: safeContext(ctx),
    });
  } catch {
    // RUM transport hiccup — never break the auth flow.
  }
};

/**
 * Correlate the current RUM session to a Peloton user id
 */
export const setAuthUser = (userId: string): void => {
  if (!ddReady() || !userId) {
    return;
  }
  try {
    datadogRum.setUser({ id: userId });
  } catch {
    // RUM transport hiccup — never break the auth flow.
  }
};

/**
 * Convenience binding for `<AuthProvider onAuthEvent={...}>`.
 *
 *   <AuthProvider onAuthEvent={onAuthProviderEvent} ... />
 */
export const onAuthProviderEvent = (
  event: AuthProviderEvent,
  ctx?: Record<string, unknown>,
): void => trackAuthEvent(`auth.${event}`, ctx);

/**
 * Convenience binding for `<CallbackHandler onCallbackEvent={...}>`.
 */
export const onCallbackEvent = (
  event: CallbackHandlerEvent,
  ctx?: Record<string, unknown>,
): void => trackAuthEvent(`auth.callback.${event}`, ctx);

/**
 * Convenience binding for `useLogout({ onLogoutEvent: ... })`.
 */
export const onLogoutEvent = (event: LogoutEvent, ctx?: Record<string, unknown>): void =>
  trackAuthEvent(`auth.logout.${event}`, ctx);

/**
 * Convenience binding for `useOauthErrorHandler({ onAuthErrorEvent: ... })`.
 *
 * Tracks the 401-recovery / 403 / 404 lifecycle events fired by the new
 * `@auth/client/useOauthErrorHandler` interceptor.
 */
export const onAuthRecoveryEvent = (
  event: AuthErrorRecoveryEvent,
  ctx?: Record<string, unknown>,
): void => trackAuthEvent(`auth.recovery.${event}`, ctx);

/**
 * Convenience binding for `useAuthClient({ onTokenResolved })` and
 * `createApolloAuthLink(getToken, { onTokenResolved })`.
 */
export const onTokenResolved = (source: ResolvedTokenSource): void =>
  trackAuthEvent(AUTH_TOKEN_RESOLVED_EVENT, { source });

/**
 * Convenience binding for `<AuthProvider onError={...}>` /
 * `<CallbackHandler onError={...}>` / `useLogout({ onError })` /
 * `useAuthClient onTokenResolved` token-fetch errors.
 */
export const onAuthError = (
  error: unknown,
  action: DDAuthErrorAction,
  ctx?: Record<string, unknown>,
): void => reportAuthError(error, action, ctx);
